import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FileText,
  Menu,
  Code,
  Brain,
  Shield,
  Globe,
  Cpu,
  Zap,
  Rocket,
  X,
  ChevronDown,
  Info,
  LogIn,
  UserPlus,
  DollarSign,
  BookOpen,
  CodeXml,
  Network,
  Bot,
  DatabaseZap,
  ShieldCheck,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./TubelightNavbar.css";
import logo from "../../images/imageNewLogo.png";

const centerNavItems = [
  { name: "Learning Process", url: "/learning-process", icon: FileText },
  // { name: "Careers", url: "/careers", icon: Briefcase },
  { name: "About Us", url: "/about", icon: Info },
  { name: "Pricing", url: "/pricing", icon: DollarSign },
  { name: "Blog", url: "/blog", icon: BookOpen },
];

const rightNavItems = [
  {
    name: "Log In",
    url: "https://andus.co.za/applicant-login-manual",
    icon: LogIn,
  },
  {
    name: "Join Now",
    url: "https://andus.co.za/applicant-register",
    icon: UserPlus,
  },
];

// Categories and programs data
const categories = [
  {
    name: "Software Development Bootcamps",
    icon: Zap,
    programs: [
      {
        name: "Software Developer Fundamentals",
        path: "/programs/junior-software-developer",
        description: "",
        icon: Code,
      },
      {
        name: "Web Developer",
        path: "/programs/webdevelopment",
        description: "",
        icon: Brain,
      },
      {
        name: "JavaScript Developer",
        path: "/programs/javascriptDeveloper",
        description: "",
        icon: Code,
      },

      {
        name: "Python Developer",
        path: "/programs/pythonDeveloper",
        description: "",
        icon: Cpu,
      },
    ],
  },
  {
    name: "AI Development Bootcamps",
    icon: Zap,
    programs: [
      {
        name: "AI Developer Fundamentals",
        path: "/programs/AIDeveloper",
        description: "",
        icon: Globe,
      },
      {
        // UPDATED: was blocked/upcoming (path: "")
        name: "Data Science with Python",
        path: "/programs/data-science-python",
        description: "",
        icon: DatabaseZap,
      },
      {
        name: "Applied Python and GUI",
        path: "/programs/applied-python-gui",
        description: "",
        icon: Code,
      },

      {
        // UPDATED: was blocked/upcoming (path: "")
        name: "Machine Learning Engineer",
        path: "/programs/machine-learning-computer-vision",
        description: "",
        icon: Cpu,
      },
      {
        // UPDATED: was blocked/upcoming (path: "")
        name: "Deep Learning and NLP",
        path: "/programs/deep-learning-nlp",
        description: "",
        icon: Network,
      },
    ],
  },
  {
    // UPDATED: was "Masterclass"
    name: "School of generative AI",
    icon: Rocket,
    programs: [
      {
        name: "AI in the Workplace - 2 Day Master class",
        path: "/programs/AITools",
        description: "",
        icon: Zap,
      },
      {
        name: "AI Productivity & Agentic Workflows with Python",
        path: "/programs/ai-productivity-python",
        description: "",
        icon: CodeXml,
      },
      {
        name: "AI Productivity & Agentic Workflows",
        path: "/programs/ai-productivity",
        description: "",
        icon: Bot,
      },
    ],
  },
  {
    // unchanged
    name: "Cyber Security",
    icon: Shield,
    programs: [
      {
        name: "Cyber Security Bootcamp",
        path: "/programs/cyber-security",
        description: "",
        icon: ShieldCheck,
      },
    ],
  },
];

const isExternalUrl = (url) => /^https?:\/\//i.test(url);

export default function TubelightNavbar({ className = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  const [activeTab, setActiveTab] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [showProgramsDropdown, setShowProgramsDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileProgramsDropdown, setShowMobileProgramsDropdown] =
    useState(false);
  const [openMobileCategories, setOpenMobileCategories] = useState(
    () => new Set(),
  );

  // Close mobile programs dropdown on route change
  useEffect(() => {
    setShowMobileProgramsDropdown(false);
    setShowProgramsDropdown(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const flatPrograms = useMemo(
    () =>
      categories.flatMap((category) =>
        category.programs.map((program) => ({
          ...program,
          category: category.name,
        })),
      ),
    [],
  );

  // Handle program selection
  const handleProgramSelect = (path) => {
    if (!path) return;
    navigate(path);
    setShowProgramsDropdown(false);
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
  };

  // Handle programs dropdown toggle
  const toggleProgramsDropdown = () => {
    setShowProgramsDropdown(!showProgramsDropdown);
    setSelectedCategory(null); // Reset category selection when opening dropdown
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  // Handle back to categories
  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  // Mobile programs dropdown handlers
  const toggleMobileProgramsDropdown = () => {
    setShowMobileProgramsDropdown(!showMobileProgramsDropdown);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileCategory = (categoryName) => {
    setOpenMobileCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const handleMobileProgramSelect = (path) => {
    if (!path) return;
    navigate(path);
    setShowMobileProgramsDropdown(false);
    setIsMobileMenuOpen(false);
  };

  const closeMobileOverlays = useCallback(() => {
    setShowMobileProgramsDropdown(false);
    setIsMobileMenuOpen(false);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const programsDropdown = document.querySelector(
        ".programs-dropdown-container",
      );

      if (programsDropdown && !programsDropdown.contains(event.target)) {
        setShowProgramsDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Scroll detection
  const handleScroll = useCallback(() => {
    const currentScrollTop = window.scrollY;
    const scrollThreshold = 3; // Reduced threshold for more responsive behavior

    // Only trigger hide/show if we've scrolled more than the threshold
    if (Math.abs(currentScrollTop - lastScrollTop) > scrollThreshold) {
      // Hide when scrolling down, show when scrolling up
      const isScrollingDown =
        currentScrollTop > lastScrollTop && currentScrollTop > 30; // Reduced minimum scroll distance
      setIsScrollingDown(isScrollingDown);
      setLastScrollTop(currentScrollTop);
    }
  }, [lastScrollTop]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const allItems = [...centerNavItems, ...rightNavItems];
    const matchingItem = allItems.find(
      (item) => item.url === location.pathname,
    );
    // Only set active tab if we're not on the home page and found a matching item
    if (location.pathname !== "/" && matchingItem) {
      setActiveTab(matchingItem.name);
    } else {
      setActiveTab(""); // No active tab when on home page
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsMedium(window.innerWidth < 1190);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setShowMobileProgramsDropdown(false);
  };

  const renderTopNavItem = (item, onClick) => {
    const Icon = item.icon;
    const isActive = activeTab === item.name;

    const common = {
      onClick,
      className: `tubelight-navbar-link ${isActive ? "active" : ""}`,
    };

    // IMPORTANT: external items should not use react-router Link
    if (isExternalUrl(item.url)) {
      return (
        <a key={item.name} href={item.url} {...common}>
          <span className="tubelight-navbar-label desktop">{item.name}</span>
          <span className="tubelight-navbar-label mobile">
            <Icon size={18} strokeWidth={2.5} />
          </span>
          {isActive && (
            <motion.div
              layoutId="lamp"
              className="tubelight-navbar-lamp-bg"
              initial={false}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="tubelight-navbar-lamp-glow" />
            </motion.div>
          )}
        </a>
      );
    }

    return (
      <Link key={item.name} to={item.url} {...common}>
        <span className="tubelight-navbar-label desktop">{item.name}</span>
        <span className="tubelight-navbar-label mobile">
          <Icon size={18} strokeWidth={2.5} />
        </span>
        {isActive && (
          <motion.div
            layoutId="lamp"
            className="tubelight-navbar-lamp-bg"
            initial={false}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="tubelight-navbar-lamp-glow" />
          </motion.div>
        )}
      </Link>
    );
  };

  return (
    <div
      className={`tubelight-navbar-fixed ${isHomePage ? "homepage-navbar" : ""} z-50 ${className} ${
        isScrollingDown ? "navbar-hidden" : ""
      }`}
    >
      <div className="tubelight-navbar-inner">
        {/* Logo Section */}
        <div className="tubelight-navbar-logo">
          <Link to="/" className="tubelight-navbar-logo-link">
            <motion.img
              src={logo}
              alt="TPC Logo"
              className="tubelight-navbar-logo-image"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Link>
        </div>

        {/* Spacer to push icons right on mobile */}
        {isMobile && <div style={{ flex: 1 }} />}

        {/* Programs Dropdown Button - Show on mobile and medium screens */}
        {isMedium && (
          <div className="tubelight-navbar-mobile-programs">
            <span className="mobile-programs-label">Programmes</span>
            <button
              className="mobile-programs-button"
              onClick={toggleMobileProgramsDropdown}
              aria-label="Toggle mobile programs dropdown"
            >
              <ChevronDown
                size={24}
                className={`mobile-programs-icon ${
                  showMobileProgramsDropdown ? "rotated" : ""
                }`}
              />
            </button>
          </div>
        )}

        {/* Hamburger Menu - Always right */}
        <div className="tubelight-navbar-mobile-menu">
          <button
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Center Navigation Items - Hidden on Mobile and Medium */}
        {!isMedium && (
          <div className="tubelight-navbar-items-center mobile-hidden">
            {centerNavItems
              .filter((item) => item.name !== "Programs")
              .map((item) =>
                renderTopNavItem(item, () => setActiveTab(item.name)),
              )}

            {/* Programs Dropdown (desktop only) */}
            <div className="programs-dropdown-container-left">
              <button
                className={`programs-dropdown-button ${
                  showProgramsDropdown ? "active" : ""
                }`}
                onClick={toggleProgramsDropdown}
                aria-label="Show all programs"
              >
                <span className="dropdown-label">Programmes</span>
                <ChevronDown size={16} className="dropdown-arrow" />
              </button>

              {showProgramsDropdown && (
                <div className="programs-dropdown">
                  <button
                    className="dropdown-close-button"
                    onClick={() => setShowProgramsDropdown(false)}
                    aria-label="Close dropdown"
                  >
                    <X size={16} />
                  </button>

                  <div className="dropdown-two-column-layout">
                    <div className="dropdown-categories-column">
                      <div className="dropdown-categories-header">
                        <span>Categories</span>
                      </div>

                      {categories.map((category, index) => {
                        return (
                          <div
                            key={index}
                            className={`category-dropdown-item ${
                              selectedCategory?.name === category.name
                                ? "active"
                                : ""
                            }`}
                            onClick={() => handleCategorySelect(category)}
                          >
                            <div className="category-dropdown-content">
                              <div className="category-dropdown-name">
                                {category.name}
                              </div>
                              <div className="category-dropdown-count">
                                {category.programs.length} programs
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="dropdown-programs-column">
                      {selectedCategory ? (
                        <>
                          <div className="dropdown-programs-header">
                            <span>{selectedCategory.name}</span>
                          </div>

                          {selectedCategory.programs.map((program, index) => {
                            const Icon = program.icon;
                            const isDisabled =
                              program.isUpcoming || !program.path;

                            return (
                              <div
                                key={index}
                                className={`program-dropdown-item ${
                                  isDisabled ? "upcoming" : ""
                                }`}
                                onClick={
                                  isDisabled
                                    ? undefined
                                    : () => handleProgramSelect(program.path)
                                }
                                style={
                                  isDisabled
                                    ? { cursor: "not-allowed", opacity: 0.6 }
                                    : {}
                                }
                              >
                                <div className="program-dropdown-icon">
                                  <Icon size={18} />
                                </div>
                                <div className="program-dropdown-content">
                                  <div className="program-dropdown-name">
                                    {program.name}
                                    {isDisabled && (
                                      <span className="upcoming-badge">
                                        Coming Soon
                                      </span>
                                    )}
                                  </div>
                                  <div className="program-dropdown-description">
                                    {program.description}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="dropdown-select-category">
                          <div className="select-category-icon">
                            <img
                              src={logo}
                              alt="TPC Logo"
                              className="select-category-logo"
                            />
                          </div>
                          <div className="select-category-text">
                            <h4>Select a Category</h4>
                            <p>
                              Choose a category from the left to view available
                              programmes
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Navigation Items - Hidden on Mobile and Medium */}
        {!isMedium && (
          <div className="tubelight-navbar-items-right mobile-hidden">
            {rightNavItems.map((item) =>
              renderTopNavItem(item, () => setActiveTab(item.name)),
            )}
          </div>
        )}
      </div>

      {isMedium && (showMobileProgramsDropdown || isMobileMenuOpen) && (
        <div className="tpc-mobile-panels">
          {showMobileProgramsDropdown && (
            <div className="tpc-mobile-panel">
              <div className="tpc-mobile-panel-header">
                <span>Programmes</span>
              </div>

              <div className="tpc-mobile-panel-list">
                {categories.map((category) => {
                  const isOpen = openMobileCategories.has(category.name);
                  return (
                    <div
                      key={category.name}
                      className="tpc-mobile-panel-category"
                    >
                      <button
                        type="button"
                        className={`tpc-mobile-panel-category-title ${
                          isOpen ? "is-open" : ""
                        }`}
                        onClick={() => toggleMobileCategory(category.name)}
                        aria-expanded={isOpen}
                      >
                        <span>{category.name}</span>
                        <ChevronDown size={14} />
                      </button>

                      <div
                        className={`tpc-mobile-panel-category-items ${
                          isOpen ? "is-open" : ""
                        }`}
                      >
                        {category.programs.map((program) => {
                          const Icon = program.icon;
                          const key = `${category.name}-${program.name}`;
                          const isDisabled =
                            program.isUpcoming || !program.path;

                          if (isDisabled) {
                            return (
                              <div
                                key={key}
                                className="tpc-mobile-panel-item is-upcoming"
                              >
                                <span className="tpc-mobile-panel-item-icon">
                                  <Icon size={16} />
                                </span>
                                <span className="tpc-mobile-panel-item-label">
                                  {program.name}
                                </span>
                                <span className="tpc-mobile-panel-item-tag">
                                  Soon
                                </span>
                              </div>
                            );
                          }
                          return (
                            <button
                              key={key}
                              type="button"
                              className="tpc-mobile-panel-item"
                              onClick={() =>
                                handleMobileProgramSelect(program.path)
                              }
                            >
                              <span className="tpc-mobile-panel-item-icon">
                                <Icon size={16} />
                              </span>
                              <span className="tpc-mobile-panel-item-label">
                                {program.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isMobileMenuOpen && (
            <div className="tpc-mobile-panel">
              <div className="tpc-mobile-panel-header">
                <span>Menu</span>
                <button
                  type="button"
                  className="tpc-mobile-panel-close"
                  onClick={closeMobileOverlays}
                  aria-label="Close mobile menu"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="tpc-mobile-panel-list">
                {[...rightNavItems, ...centerNavItems].map((item) => {
                  const Icon = item.icon;

                  if (isExternalUrl(item.url)) {
                    return (
                      <a
                        key={item.name}
                        href={item.url}
                        onClick={() => {
                          setActiveTab(item.name);
                          closeMobileOverlays();
                        }}
                        className="tpc-mobile-panel-item"
                      >
                        <span className="tpc-mobile-panel-item-icon">
                          <Icon size={16} />
                        </span>
                        <span className="tpc-mobile-panel-item-label">
                          {item.name}
                        </span>
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.url}
                      onClick={() => {
                        setActiveTab(item.name);
                        closeMobileOverlays();
                      }}
                      className="tpc-mobile-panel-item"
                    >
                      <span className="tpc-mobile-panel-item-icon">
                        <Icon size={16} />
                      </span>
                      <span className="tpc-mobile-panel-item-label">
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
